import type { Point } from './camera';
import type { HandleId } from './handles';
import type { Box } from './tools/tool';

/** The visible part of an image, normalized on its source (non-destructive: the asset is never cut). */
export type Crop = { x: number; y: number; w: number; h: number };
export const FULL_CROP: Crop = Object.freeze({ x: 0, y: 0, w: 1, h: 1 });
export const MIN_CROP = 0.01;

const round = (v: number) => Math.round(v * 1e9) / 1e9;

/** Inside [0, 1] with at least MIN_CROP on each side; anything non-finite is the full image. */
export function clampCrop(c: Crop): Crop {
  if (![c.x, c.y, c.w, c.h].every(Number.isFinite)) return FULL_CROP;
  const axis = (lo: number, size: number) => {
    const a = Math.min(Math.max(lo, 0), 1 - MIN_CROP);
    const b = Math.min(Math.max(lo + size, a + MIN_CROP), 1);
    return [round(a), round(b - a)] as const;
  };
  const [x, w] = axis(c.x, c.w);
  const [y, h] = axis(c.y, c.h);
  return { x, y, w, h };
}

/** World box of the whole source image of an object `box` showing `crop` (unrotated). */
export function fullImageBox(box: Box, crop: Crop): Box {
  const w = box.w / crop.w;
  const h = box.h / crop.h;
  return { x: box.x - crop.x * w, y: box.y - crop.y * h, w, h };
}

/** The box that shows `to` at the same source scale as `box` showing `from`. */
export function cropBox(box: Box, from: Crop, to: Crop): Box {
  const f = fullImageBox(box, from);
  return { x: round(f.x + to.x * f.w), y: round(f.y + to.y * f.h), w: round(to.w * f.w), h: round(to.h * f.h) };
}

/** The crop after dragging its `handle` to world point `p`, over the full image `full`. */
export function cropFrom(crop: Crop, handle: HandleId, p: Point, full: Box): Crop {
  const u = (p.x - full.x) / full.w;
  const v = (p.y - full.y) / full.h;
  let [x0, y0, x1, y1] = [crop.x, crop.y, crop.x + crop.w, crop.y + crop.h];
  if (handle.includes('w')) x0 = Math.min(u, x1 - MIN_CROP);
  if (handle.includes('e')) x1 = Math.max(u, x0 + MIN_CROP);
  if (handle.includes('n')) y0 = Math.min(v, y1 - MIN_CROP);
  if (handle.includes('s')) y1 = Math.max(v, y0 + MIN_CROP);
  return clampCrop({ x: x0, y: y0, w: x1 - x0, h: y1 - y0 });
}
